import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { C, Btn, Input, Card } from "../components/UI";

// Estados brasileiros - sigla usada no banco para permitir filtros
const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
  "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

// Campo de selecao de cidade. Carrega os municipios do estado escolhido
// usando a base publica do IBGE. Se a consulta falhar, vira campo de texto.
function SelectCidade({ uf, value, onChange }) {
  const [cidades, setCidades] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (!uf) {
      setCidades([]);
      return;
    }
    let ativo = true;
    setCarregando(true);
    setFalhou(false);

    fetch(`https://servicosdados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
      .then(r => r.json())
      .then(dados => {
        if (!ativo) return;
        setCidades(dados.map(m => m.nome));
        setCarregando(false);
      })
      .catch(() => {
        if (!ativo) return;
        setFalhou(true);
        setCarregando(false);
      });

    return () => { ativo = false; };
  }, [uf]);

  const estiloCampo = {
    border: `0.5px solid ${C.border}`,
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    color: C.text,
    background: "#fff",
    width: "100%",
    boxSizing: "border-box"
  };

  // Sem estado escolhido ou sem acesso ao IBGE: campo de texto simples
  if (falhou || !uf) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Cidade</label>
        <input
          value={value}
          onChange={onChange}
          disabled={!uf}
          placeholder={uf ? "Digite sua cidade" : "Escolha o estado primeiro"}
          style={{ ...estiloCampo, background: uf ? "#fff" : C.grayBg }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Cidade</label>
      <select value={value} onChange={onChange} disabled={carregando} style={estiloCampo}>
        <option value="">{carregando ? "Carregando cidades..." : "Selecione a cidade"}</option>
        {cidades.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

// Campo de selecao de estado reaproveitado nos dois cadastros
function SelectUF({ value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Estado</label>
      <select
        value={value}
        onChange={onChange}
        style={{ border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 14, color: C.text, background: "#fff" }}
      >
        <option value="">UF</option>
        {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
      </select>
    </div>
  );
}

// ── Shared layout ──────────────────────────────────────────────────────────────
function AuthLayout({ children, title, sub }) {
  return (
    <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${C.rosePale} 0%, #faf9f7 60%)`, padding: 24 }}>
      <Card style={{ width: "100%", maxWidth: 440, padding: "40px 36px" }} className="anim-1">
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: C.roseMid, marginBottom: 6 }}>WoMom</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, marginBottom: 6 }}>{title}</h1>
          <p style={{ fontSize: 14, color: C.textSec }}>{sub}</p>
        </div>
        {children}
      </Card>
    </div>
  );
}

// ── Login ──────────────────────────────────────────────────────────────────────
export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useApp();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!email || !password) { setError("Preencha todos os campos."); return; }
    setLoading(true);
    setError("");
    
    // Timeout de 10 segundos
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError("Tempo esgotado. Verifique sua conexao e tente novamente.");
    }, 10000);
    
    try {
      const { data, error: authError } = await login(email, password);
      clearTimeout(timeoutId);
      
      if (authError) {
        setLoading(false);
        if (authError.message?.includes("Invalid login")) {
          setError("E-mail ou senha incorretos.");
        } else if (authError.message?.includes("Email not confirmed")) {
          setError("Por favor, confirme seu e-mail antes de entrar.");
        } else {
          setError(authError.message || "Erro ao fazer login. Tente novamente.");
        }
        return;
      }

      if (data?.user) {
        // O destino vem do tipo da propria conta, nao de uma escolha na tela
        const tipoDaConta = data.user.user_metadata?.user_type;
        navigate(tipoDaConta === "company" ? "/dashboard" : "/");
      } else {
        setLoading(false);
        setError("Erro ao fazer login. Tente novamente.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      setLoading(false);
      setError("Erro de conexao. Tente novamente.");
    }
  };

  return (
    <AuthLayout title="Bem-vinda de volta" sub="Entre na sua conta para continuar">
      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
        <Input label="E-mail" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="seu@email.com" required />
        <Input label="Senha" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
      </div>

      {error && <p style={{ fontSize: 13, color: C.red, marginBottom: 14 }}>{error}</p>}

      <div style={{ textAlign: "right", marginBottom: 20 }}>
        <span style={{ fontSize: 13, color: C.roseMid, cursor: "pointer" }}>Esqueci minha senha</span>
      </div>

      <Btn full onClick={handleSubmit} disabled={loading}>{loading ? "Entrando..." : "Entrar"}</Btn>

      <p style={{ textAlign: "center", fontSize: 13, color: C.textSec, marginTop: 20 }}>
        Não tem conta?{" "}
        <span onClick={() => navigate("/cadastro")} style={{ color: C.roseMid, cursor: "pointer", fontWeight: 500 }}>Cadastre-se grátis</span>
      </p>
    </AuthLayout>
  );
}

// ── Selecionar tipo de conta ───────────────────────────────────────────────────
export function SelectAccountType() {
  const navigate = useNavigate();
  
  return (
    <AuthLayout title="Criar conta" sub="Escolha o tipo de conta que deseja criar">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <button
          onClick={() => navigate("/cadastro-mulher")}
          style={{
            padding: "20px 24px",
            border: `1.5px solid ${C.roseBorder}`,
            borderRadius: 12,
            background: C.rosePale,
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.2s"
          }}
          onMouseOver={e => e.currentTarget.style.borderColor = C.roseMid}
          onMouseOut={e => e.currentTarget.style.borderColor = C.roseBorder}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: C.text }}>Sou Mulher</div>
          <p style={{ fontSize: 13, color: C.textSec, margin: 0, lineHeight: 1.5 }}>
            Avalie empresas, compartilhe experiencias e encontre ambientes que valorizam mulheres.
          </p>
        </button>
        
        <button
          onClick={() => navigate("/cadastro-empresa")}
          style={{
            padding: "20px 24px",
            border: `1.5px solid ${C.border}`,
            borderRadius: 12,
            background: "#fff",
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.2s"
          }}
          onMouseOver={e => e.currentTarget.style.borderColor = C.roseMid}
          onMouseOut={e => e.currentTarget.style.borderColor = C.border}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: C.text }}>Sou Empresa</div>
          <p style={{ fontSize: 13, color: C.textSec, margin: 0, lineHeight: 1.5 }}>
            Crie seu perfil, mostre suas politicas e atraia talentos femininos qualificados.
          </p>
        </button>
      </div>
      
      <p style={{ textAlign: "center", fontSize: 13, color: C.textSec, marginTop: 24 }}>
        Ja tem conta?{" "}
        <span onClick={() => navigate("/login")} style={{ color: C.roseMid, cursor: "pointer", fontWeight: 500 }}>Entrar</span>
      </p>
    </AuthLayout>
  );
}

// ── Register (mulher) ──────────────────────────────────────────────────────────
export function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", password: "", passwordConfirm: "", city: "", state: "", sector: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { signUp } = useApp();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) {
      setError("Preencha todos os campos obrigatórios.");
      return;
    }
    if (form.password.length < 6) {
      setError("A senha deve ter no mínimo 6 caracteres.");
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setError("As senhas não são iguais.");
      return;
    }
    if (!form.city || !form.state) {
      setError("Informe sua cidade e estado.");
      return;
    }

    setLoading(true);
    setError("");

    const { data, error: authError } = await signUp(form.email, form.password, {
      name: form.name,
      user_type: "woman",
      city: form.city,
      state: form.state,
      area: form.sector,
    });

    setLoading(false);

    // Verificar se houve erro
    if (authError) {
      if (authError.message?.includes("already registered") || authError.message?.includes("User already registered")) {
        setError("Este e-mail ja esta cadastrado. Faca login ou use outro e-mail.");
      } else if (authError.message?.includes("Invalid email")) {
        setError("E-mail invalido. Verifique e tente novamente.");
      } else if (authError.message?.includes("Password")) {
        setError("Senha muito fraca. Use letras e numeros.");
      } else if (authError.message?.includes("security purposes") || authError.message?.includes("rate limit")) {
        setError("Aguarde alguns segundos antes de tentar novamente.");
      } else if (authError.message?.includes("email") && authError.message?.includes("confirm")) {
        // Email enviado mas erro de confirmacao - considerar sucesso
        setSuccess(true);
        return;
      } else {
        setError(authError.message || "Erro ao criar conta. Tente novamente.");
      }
      return;
    }

    // Se chegou aqui, conta criada com sucesso
    setSuccess(true);
  };

  if (success) {
    return (
      <AuthLayout title="Verifique seu e-mail" sub="Quase la! Confirme seu cadastro">
        <div style={{ textAlign: "center" }}>
          <div style={{ 
            width: 80, 
            height: 80, 
            borderRadius: "50%", 
            background: C.rosePale, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 24px",
            border: `2px solid ${C.roseBorder}`
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.roseMid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <p style={{ fontSize: 15, color: C.text, marginBottom: 8, fontWeight: 500 }}>
            Enviamos um e-mail para:
          </p>
          <p style={{ fontSize: 15, color: C.roseMid, marginBottom: 20, fontWeight: 600 }}>
            {form.email}
          </p>
          <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24, lineHeight: 1.6 }}>
            Clique no link que enviamos para confirmar seu cadastro e ativar sua conta.
          </p>
          <div style={{ 
            background: C.grayBg, 
            borderRadius: 10, 
            padding: "14px 16px", 
            fontSize: 13, 
            color: C.textSec, 
            marginBottom: 24,
            lineHeight: 1.5
          }}>
            Nao recebeu o e-mail? Verifique sua caixa de spam ou aguarde alguns minutos.
          </div>
          <Btn variant="ghost" onClick={() => navigate("/login")}>Ja confirmei, ir para Login</Btn>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Crie sua conta" sub="Gratuito para sempre — para mulheres">
      <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
        {[1, 2].map(s => (
          <div key={s} style={{ flex: 1, height: 3, borderRadius: 100, background: step >= s ? C.roseMid : C.border }} />
        ))}
      </div>

      {error && <p style={{ fontSize: 13, color: C.red, marginBottom: 14 }}>{error}</p>}

      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input label="Seu nome" value={form.name} onChange={set("name")} placeholder="Como prefere ser chamada" required />
          <Input label="E-mail" type="email" value={form.email} onChange={set("email")} placeholder="seu@email.com" required />
          <Input label="Senha" type="password" value={form.password} onChange={set("password")} placeholder="Mínimo 6 caracteres" required />
          <Input label="Confirmar senha" type="password" value={form.passwordConfirm} onChange={set("passwordConfirm")} placeholder="Digite a senha novamente" required />
          <Btn full onClick={() => {
            if (!form.name || !form.email || !form.password) {
              setError("Preencha todos os campos.");
              return;
            }
            if (form.password.length < 6) {
              setError("A senha deve ter no mínimo 6 caracteres.");
              return;
            }
            if (form.password !== form.passwordConfirm) {
              setError("As senhas não são iguais.");
              return;
            }
            setError("");
            setStep(2);
          }}>Continuar →</Btn>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
            <SelectUF value={form.state} onChange={(e) => setForm(f => ({ ...f, state: e.target.value, city: "" }))} />
            <SelectCidade uf={form.state} value={form.city} onChange={set("city")} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Área de atuação</label>
            <select value={form.sector} onChange={set("sector")} style={{ border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 14, color: C.text, background: "#fff" }}>
              <option value="">Selecione...</option>
              {["Tecnologia", "Financeiro", "Saúde", "Educação", "Jurídico", "Marketing", "Engenharia", "Outro"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ background: C.rosePale, border: `0.5px solid ${C.roseBorder}`, borderRadius: 10, padding: "12px 14px", fontSize: 13, color: C.textSec, lineHeight: 1.6 }}>
            🔒 Sua identidade é protegida. Você decide o que aparece em cada avaliação.
          </div>
          <Btn full onClick={handleSubmit} disabled={loading}>{loading ? "Criando conta..." : "Criar minha conta grátis"}</Btn>
          <Btn variant="ghost" full onClick={() => setStep(1)} style={{ fontSize: 13 }}>← Voltar</Btn>
        </div>
      )}

      <p style={{ textAlign: "center", fontSize: 13, color: C.textSec, marginTop: 20 }}>
        Já tem conta?{" "}
        <span onClick={() => navigate("/login")} style={{ color: C.roseMid, cursor: "pointer", fontWeight: 500 }}>Entrar</span>
      </p>
    </AuthLayout>
  );
}

// ── Register (empresa) ─────────────────────────────────────────────────────────
export function RegisterCompany() {
  const [form, setForm] = useState({ name: "", cnpj: "", email: "", password: "", passwordConfirm: "", site: "", sector: "", size: "", city: "", state: "", phone: "", contactName: "", contactRole: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { signUp } = useApp();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.cnpj || !form.email || !form.password) {
      setError("Preencha todos os campos obrigatórios.");
      return;
    }
    if (form.password.length < 6) {
      setError("A senha deve ter no mínimo 6 caracteres.");
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setError("As senhas não são iguais.");
      return;
    }
    if (!form.city || !form.state) {
      setError("Informe a cidade e o estado da empresa.");
      return;
    }
    if (!form.contactName) {
      setError("Informe quem e a pessoa responsavel pelo cadastro.");
      return;
    }

    setLoading(true);
    setError("");

    const { error: authError } = await signUp(form.email, form.password, {
      name: form.name,
      user_type: "company",
      cnpj: form.cnpj,
      site: form.site,
      sector: form.sector,
      size: form.size,
      city: form.city,
      state: form.state,
      phone: form.phone,
      contact_name: form.contactName,
      contact_role: form.contactRole,
    });

    setLoading(false);

    if (authError) {
      if (authError.message?.includes("already registered") || authError.message?.includes("User already registered")) {
        setError("Este e-mail já está cadastrado. Faça login ou use outro e-mail.");
      } else if (authError.message?.includes("Invalid email")) {
        setError("E-mail inválido. Verifique e tente novamente.");
      } else if (authError.message?.includes("Password")) {
        setError("Senha muito fraca. Use letras e números.");
      } else {
        setError(authError.message || "Erro ao criar conta. Tente novamente.");
      }
      return;
    }

    setSuccess(true);
  };

  if (success) {
    return (
      <AuthLayout title="Verifique seu e-mail" sub="Quase la! Confirme seu cadastro">
        <div style={{ textAlign: "center" }}>
          <div style={{ 
            width: 80, 
            height: 80, 
            borderRadius: "50%", 
            background: C.rosePale, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 24px",
            border: `2px solid ${C.roseBorder}`
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.roseMid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <p style={{ fontSize: 15, color: C.text, marginBottom: 8, fontWeight: 500 }}>
            Enviamos um e-mail para:
          </p>
          <p style={{ fontSize: 15, color: C.roseMid, marginBottom: 20, fontWeight: 600 }}>
            {form.email}
          </p>
          <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24, lineHeight: 1.6 }}>
            Clique no link que enviamos para confirmar seu cadastro, ativar sua conta e escolher seu plano.
          </p>
          <div style={{ 
            background: C.grayBg, 
            borderRadius: 10, 
            padding: "14px 16px", 
            fontSize: 13, 
            color: C.textSec, 
            marginBottom: 24,
            lineHeight: 1.5
          }}>
            Nao recebeu o e-mail? Verifique sua caixa de spam ou aguarde alguns minutos.
          </div>
          <Btn variant="ghost" onClick={() => navigate("/login")}>Ja confirmei, ir para Login</Btn>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Perfil da empresa" sub="Crie seu perfil e escolha um plano">
      {error && <p style={{ fontSize: 13, color: C.red, marginBottom: 14 }}>{error}</p>}
      
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Input label="Nome da empresa" value={form.name} onChange={set("name")} placeholder="Ex: Nubank" required />
        <Input label="CNPJ" value={form.cnpj} onChange={set("cnpj")} placeholder="00.000.000/0000-00" required />
        <Input label="E-mail corporativo" type="email" value={form.email} onChange={set("email")} placeholder="rh@empresa.com" required />
        <Input label="Senha" type="password" value={form.password} onChange={set("password")} placeholder="Mínimo 6 caracteres" required />
        <Input label="Confirmar senha" type="password" value={form.passwordConfirm} onChange={set("passwordConfirm")} placeholder="Digite a senha novamente" required />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
          <SelectUF value={form.state} onChange={(e) => setForm(f => ({ ...f, state: e.target.value, city: "" }))} />
          <SelectCidade uf={form.state} value={form.city} onChange={set("city")} />
        </div>
        <Input label="Telefone" value={form.phone} onChange={set("phone")} placeholder="(11) 90000-0000" />
        <Input label="Site" value={form.site} onChange={set("site")} placeholder="https://empresa.com" />
        <div style={{ borderTop: `0.5px solid ${C.border}`, paddingTop: 16, marginTop: 4 }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 12 }}>Pessoa responsavel pelo cadastro</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Input label="Nome" value={form.contactName} onChange={set("contactName")} placeholder="Maria Silva" required />
            <Input label="Cargo" value={form.contactRole} onChange={set("contactRole")} placeholder="Gerente de RH" />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Setor</label>
            <select value={form.sector} onChange={set("sector")} style={{ border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13, background: "#fff", color: C.text }}>
              <option value="">Selecione...</option>
              {["Tecnologia", "Financeiro", "Saúde", "Varejo", "Educação", "Outro"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Porte</label>
            <select value={form.size} onChange={set("size")} style={{ border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13, background: "#fff", color: C.text }}>
              <option value="">Selecione...</option>
              {["1-50", "51-200", "201-1000", "1000+"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <Btn full onClick={handleSubmit} disabled={loading}>{loading ? "Criando perfil..." : "Criar perfil e ver planos →"}</Btn>
      </div>
    </AuthLayout>
  );
}
